import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import { Category } from '../../types/content';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import { confirmAction } from '../../utils/confirmAction';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';

const CATEGORY_KINDS = [
  { value: 'Course', label: 'Course menu' },
  { value: 'Lab', label: 'Lab' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Project', label: 'Project' },
  { value: 'Gig', label: 'Gig' },
  { value: 'Event', label: 'Event' },
] as const;

const kindLabel = (kind: string) => {
  if (kind === 'Article' || kind === 'Course') return 'Course menu';
  if (kind === 'Lab') return 'Lab category';
  if (kind === 'Technology') return 'Lab technology';
  if (kind === 'Project') return 'Project category';
  if (kind === 'Gig') return 'Gig category';
  if (kind === 'Event') return 'Event category';
  return kind;
};

const categoryKind = (item: Category) => (item.kind === 'Article' ? 'Course' : item.kind);

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('Course');
  const [filterKinds, setFilterKinds] = useState<string[]>(() => CATEGORY_KINDS.map((item) => item.value));
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editKind, setEditKind] = useState('Course');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 8000);

  const load = () => contentApi.listCategories().then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  useEffect(() => { load(); }, []);

  const startEdit = (item: Category) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditKind(categoryKind(item));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditKind('Course');
  };

  const addCategory = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await contentApi.saveCategory({ name: trimmed, kind, isActive: true, displayOrder: categories.length + 1 });
      setName('');
      setMessage('Category added.');
      await load();
    } catch {
      setError('Unable to add the category.');
    } finally {
      setSaving(false);
    }
  };

  const saveInline = async (item: Category) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setError('Enter a category name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await contentApi.saveCategory(
        { ...item, name: trimmed, kind: editKind, isActive: item.isActive, displayOrder: item.displayOrder },
        item.id
      );
      setMessage('Category updated.');
      cancelEdit();
      await load();
    } catch {
      setError('Unable to update the category.');
    } finally {
      setSaving(false);
    }
  };

  const visibleCategories = useMemo(
    () => categories.filter((item) => filterKinds.includes(categoryKind(item))),
    [categories, filterKinds]
  );

  const toggleFilter = (value: string) => {
    setFilterKinds((current) => (
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    ));
  };

  const move = async (index: number, dir: -1 | 1) => {
    const next = [...categories];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await contentApi.reorderCategories(next.map((c, i) => ({ id: c.id, displayOrder: i + 1 })));
    load();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
      <p className="mt-2 text-gray-600">Course menus use the Course type. Labs share those same categories through the course they belong to. Click Edit to change a name or type in the list.</p>
      <Card className="mt-6 p-5">
        <form className="flex flex-wrap gap-2 items-end" onSubmit={async (e) => { e.preventDefault(); await addCategory(); }}>
          <Input id="cat" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="block text-sm font-medium text-gray-700">Type
            <select className="mt-1 rounded-md border-gray-300 h-10" value={kind} onChange={(e) => setKind(e.target.value)}>
              {CATEGORY_KINDS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={saving}>Add</Button>
          <ActionStatus className="" message={message} error={error} />
        </form>
      </Card>
      <fieldset className="mt-6">
        <legend className="text-sm font-medium text-gray-700">Filter by type</legend>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
          {CATEGORY_KINDS.map((item) => (
            <label key={item.value} className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filterKinds.includes(item.value)}
                onChange={() => toggleFilter(item.value)}
              />
              {item.label}
            </label>
          ))}
        </div>
      </fieldset>
      <ul className="mt-4 space-y-2">
        {visibleCategories.length === 0 && (
          <li className="text-sm text-gray-500 border rounded-lg px-3 py-3 bg-white">No categories match the selected types.</li>
        )}
        {visibleCategories.map((c) => {
          const index = categories.findIndex((item) => item.id === c.id);
          const isEditing = editingId === c.id;
          return (
          <li key={c.id} className="flex justify-between items-center gap-3 border rounded-lg px-3 py-2 bg-white">
            {isEditing ? (
              <form
                className="flex-1 flex flex-wrap items-center gap-2 min-w-0"
                onSubmit={async (e) => { e.preventDefault(); await saveInline(c); }}
              >
                <input
                  autoFocus
                  aria-label="Category name"
                  className="min-w-[10rem] flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
                />
                <select
                  aria-label="Category type"
                  className="rounded-md border-gray-300 text-sm h-9"
                  value={editKind}
                  onChange={(e) => setEditKind(e.target.value)}
                >
                  {CATEGORY_KINDS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <Button type="submit" size="sm" disabled={saving}>Save</Button>
                <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Button>
              </form>
            ) : (
              <button type="button" className="text-left min-w-0" onClick={() => startEdit(c)}>
                <p className="font-medium text-gray-900">{c.name}</p>
                <p className="text-xs text-gray-500">{kindLabel(c.kind)} · {c.isActive ? 'Active' : 'Inactive'}</p>
              </button>
            )}
            {!isEditing && (
            <div className="flex gap-2 shrink-0">
              <Button type="button" variant="ghost" size="sm" onClick={() => move(index, -1)}>Up</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => move(index, 1)}>Down</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(c)}>Edit</Button>
              {!c.isActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await contentApi.saveCategory({ ...c, isActive: true }, c.id);
                    load();
                  }}
                >
                  Activate
                </Button>
              )}
              {c.isActive && (
                <Button type="button" variant="ghost" size="sm" onClick={async () => {
                  const ok = await confirmAction({
                    title: 'Deactivate this category?',
                    description: `“${c.name}” will no longer appear in menus.`,
                    confirmLabel: 'Deactivate',
                    cancelLabel: 'Keep it',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  await contentApi.deleteCategory(c.id);
                  load();
                }}>Deactivate</Button>
              )}
            </div>
            )}
          </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CategoriesPage;
